const FORCE_LOGIN_KEY = 'forceLogin'
const FORCE_LOGIN_COOKIE = 'ps-force-login'
const SKIP_AUTH_CACHE_KEY = 'skipAuthCache'
const SKIP_AUTH_CACHE_COOKIE = 'ps-skip-auth-cache'

export const AUTH_REDIRECT_URL_KEY = 'authRedirectUrl'

function getCookieDomains(hostname: string) {
  const isIpv4Hostname = hostname
    .split('.')
    .every((part) => part.length > 0 && Number.isInteger(Number(part)) && Number(part) >= 0 && Number(part) <= 255)

  if (!hostname || hostname === 'localhost' || isIpv4Hostname) {
    return [undefined]
  }

  const parts = hostname.split('.')
  if (parts.length < 2) {
    return [undefined]
  }

  return [undefined, `.${parts.slice(-2).join('.')}`]
}

function writeCookie(name: string, value: string, expires: string) {
  const secure = window.location.protocol === 'https:' ? '; Secure' : ''

  getCookieDomains(window.location.hostname).forEach((domain) => {
    document.cookie = `${name}=${value}; expires=${expires}; path=/; SameSite=Lax${domain ? `; domain=${domain}` : ''}${secure}`
  })
}

function hasCookie(name: string, value: string) {
  return document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .some((cookie) => cookie === `${name}=${value}`)
}

function clearCookie(name: string) {
  writeCookie(name, '', 'Thu, 01 Jan 1970 00:00:00 GMT')
}

// `localStorage` can throw or evaluate to `null` (Safari ITP, private browsing, storage
// partitioning around the cross-origin Auth0 redirect) — every accessor in this file is
// wrapped through here so a broken localStorage can never crash or hang the login flow.
// The cookie-backed keys below still work even when this fails, since writeCookie/hasCookie
// don't touch localStorage.
//
// Warn once per page load rather than once per call site — a single broken-localStorage
// load touches this from several functions in the same render/effect pass, and repeating
// the warning that many times just buries other console signal.
let hasWarnedStorageUnavailable = false

function safeStorage<T>(fn: () => T, fallback: T): T
function safeStorage<T>(fn: () => T): T | undefined
function safeStorage<T>(fn: () => T, fallback?: T): T | undefined {
  try {
    return fn()
  } catch (err) {
    if (!hasWarnedStorageUnavailable) {
      hasWarnedStorageUnavailable = true
      // eslint-disable-next-line no-console
      console.warn('[react-auth0] localStorage unavailable', err)
    }
    return fallback
  }
}

export function markForceLogin() {
  safeStorage(() => localStorage.setItem(FORCE_LOGIN_KEY, 'true'))
  safeStorage(() => localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true'))
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(FORCE_LOGIN_COOKIE, '1', expires)
  writeCookie(SKIP_AUTH_CACHE_COOKIE, '1', expires)
}

// Sets the cache-bypass signal only — without the login-form prompt.
// Used when redirecting a painter from the main app to painter-view so that
// painter-view discards any stale non-painter session it has cached locally.
export function markSkipAuthCache() {
  safeStorage(() => localStorage.setItem(SKIP_AUTH_CACHE_KEY, 'true'))
  const expires = new Date(Date.now() + 5 * 60 * 1000).toUTCString()
  writeCookie(SKIP_AUTH_CACHE_COOKIE, '1', expires)
}

export function consumeForceLogin() {
  // Only honor the cookie — not the localStorage key alone.
  // The localStorage key can be stale (set by a previous logout on this origin and
  // never consumed because the cross-app SSO flow bypassed the login form). The
  // cookie has a 5-minute expiry and is always fresh when present. This means a
  // cross-app redirect from a freshly-authenticated painter will never accidentally
  // show the login form a second time due to a lingering localStorage key.
  const shouldForceLogin = hasCookie(FORCE_LOGIN_COOKIE, '1')

  safeStorage(() => localStorage.removeItem(FORCE_LOGIN_KEY)) // always clean up, even if stale
  clearCookie(FORCE_LOGIN_COOKIE)

  return shouldForceLogin
}

export function shouldSkipAuthCache() {
  // The `=== 'true'` comparison is done *inside* the safeStorage callback (not on its
  // result) so that a thrown read falls back to the fallback below directly, rather than
  // comparing `undefined === 'true'` outside the try boundary. Fail safe, not fail open:
  // when localStorage can't be read at all, we can't rule out a session that should have
  // been invalidated, so default to `true` (skip/discard the cache) rather than `false`
  // (trust it). The cookie check still runs and can also independently say `true`.
  return (
    safeStorage(() => localStorage.getItem(SKIP_AUTH_CACHE_KEY) === 'true', true) ||
    hasCookie(SKIP_AUTH_CACHE_COOKIE, '1')
  )
}

export function clearSkipAuthCache() {
  safeStorage(() => localStorage.removeItem(SKIP_AUTH_CACHE_KEY))
  clearCookie(SKIP_AUTH_CACHE_COOKIE)
}

/**
 * Remember where to return to after the Auth0 round trip. Defaults to the current
 * location including its query string — the query is load-bearing (e.g. the admin
 * masquerade link's `?masqueradeCompanyId=`), and dropping it silently lands a
 * superadmin in their own account instead of the client's.
 *
 * On a branch deploy `window.location.pathname` still carries the `/_branch/{name}`
 * prefix (it is not basename-stripped), so stashing it is also what gets the user
 * back onto the branch after Auth0 returns to the canonical origin.
 *
 * Only call this when a login redirect is actually about to happen — a stashed value
 * lives in localStorage until it's consumed by the callback, so writing it on a path
 * that never reaches /login-callback leaves a stale value that hijacks a later login.
 */
export function storeAuthRedirectUrl(url?: string) {
  safeStorage(() =>
    localStorage.setItem(AUTH_REDIRECT_URL_KEY, url ?? `${window.location.pathname}${window.location.search}`)
  )
}

export function clearAuthRedirectUrl() {
  safeStorage(() => localStorage.removeItem(AUTH_REDIRECT_URL_KEY))
}

// Safe read counterpart to storeAuthRedirectUrl/clearAuthRedirectUrl — used by
// useAuthCallback on the /login-callback page so a broken localStorage can't throw
// before handleRedirectCallback() is even called.
export function getAuthRedirectUrl(): string | null {
  return safeStorage(() => localStorage.getItem(AUTH_REDIRECT_URL_KEY), null)
}

// Reads the auth0-spa-js cached user entry directly out of localStorage — used by
// AuthProvider to seed session state on initial render, before the async check in its
// initializeAuth effect resolves. Every localStorage touch (`length`/`key`/`getItem`) and
// the JSON parse of a corrupt/partial entry are covered by the same safeStorage boundary,
// so a broken store falls back to "no cached user" instead of throwing during render.
export function getCachedAuth0User(): Record<string, any> | undefined {
  return safeStorage(() => {
    const regex = /^@@auth0spajs@@::.+::@@user@@$/

    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i)
      if (key && regex.test(key)) {
        const foundItem = localStorage.getItem(key)
        if (foundItem) {
          return JSON.parse(foundItem)
        }
      }
    }
  })
}

export function getLastLoginHint(): string | undefined {
  const lastLogin = safeStorage(() => localStorage.getItem('lastLogin'), null)

  if (!lastLogin) {
    return undefined
  }

  try {
    const parsed = JSON.parse(lastLogin)
    return typeof parsed === 'string' ? parsed : undefined
  } catch {
    return lastLogin
  }
}

export function getLoginAuthorizationParams(extraParams: Record<string, string | undefined> = {}) {
  const forceLogin = consumeForceLogin()

  return {
    login_hint: getLastLoginHint(),
    ...extraParams,
    ...(forceLogin ? { prompt: 'login' as const } : {})
  }
}
